import { useRef, useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { Badge, Button, Card, EmptyState, Input, Text } from '../components/atoms';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { AdminArea } from '../components/admin/AdminArea';
import { AdminTable } from '../components/admin/AdminTable';
import { AdminConfirmDialog, type AdminConfirmRequest } from '../components/admin/AdminConfirmDialog';
import { formatDate, formatNumber } from '../components/admin/adminFormat';
import {
  currentMonth,
  fetchAdminAiUsage,
  grantAiTopup,
  type AdminAiUsageUser,
} from '../services/adminService';

const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
const DEFAULT_TOPUP_CREDITS = 50;

/**
 * Admin → AI usage: global spend vs. cap and per-user usage for a month
 * (GET /api/v1/admin/ai-usage?month=), plus admin-granted top-ups
 * (POST /api/v1/admin/ai-usage/topups — current or future month only).
 */
export function AdminAiUsagePage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const thisMonth = currentMonth();
  const [month, setMonth] = useState(thisMonth);
  const [confirm, setConfirm] = useState<AdminConfirmRequest | null>(null);
  const [credits, setCredits] = useState(String(DEFAULT_TOPUP_CREDITS));
  // The confirm callback reads the latest typed amount.
  const creditsRef = useRef(credits);
  creditsRef.current = credits;
  const [done, setDone] = useState('');

  const validMonth = MONTH_PATTERN.test(month);
  const usage = useQuery({
    queryKey: ['admin', 'ai-usage', month],
    queryFn: () => fetchAdminAiUsage(month),
    enabled: validMonth,
    placeholderData: keepPreviousData,
  });

  const users = usage.data?.users ?? [];
  const canTopUp = validMonth && month >= thisMonth;
  const lang = i18n.language;

  function askTopup(user: AdminAiUsageUser): void {
    const who = user.email ?? user.userId;
    setDone('');
    setCredits(String(DEFAULT_TOPUP_CREDITS));
    setConfirm({
      title: t('adminArea.aiUsage.topup'),
      message: t('adminArea.aiUsage.confirmTopup', { email: who, month }),
      confirmLabel: t('adminArea.aiUsage.topupConfirm'),
      onConfirm: async () => {
        const amount = Number.parseInt(creditsRef.current, 10);
        if (!Number.isInteger(amount) || amount < 1 || amount > 10_000) throw new Error('invalid credits');
        await grantAiTopup(user.userId, amount, month);
        setDone(t('adminArea.aiUsage.topupGranted', { email: who, credits: amount }));
        await queryClient.invalidateQueries({ queryKey: ['admin', 'ai-usage'] });
      },
    });
  }

  const global = usage.data?.global;

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="wide">
          <PageHeader title={t('adminArea.title')} description={t('adminArea.aiUsage.description')} />
          <AdminArea>
            <Section title={t('adminArea.aiUsage.monthTitle')}>
              <Card padding="md">
                <div className="rg-admin-form">
                  <Input
                    type="month"
                    label={t('adminArea.aiUsage.month')}
                    value={month}
                    max={thisMonth}
                    onChange={(e) => setMonth(e.target.value)}
                    error={validMonth ? undefined : t('adminArea.aiUsage.monthInvalid')}
                    fullWidth
                  />
                </div>
              </Card>
              {global && (
                <dl className="rg-admin-summary">
                  <div className="rg-admin-summary__item">
                    <dt className="rg-admin-summary__term">{t('adminArea.aiUsage.spent')}</dt>
                    <dd className="rg-admin-summary__value">US$ {formatNumber(global.spentUsd, lang, 2)}</dd>
                  </div>
                  <div className="rg-admin-summary__item">
                    <dt className="rg-admin-summary__term">{t('adminArea.aiUsage.cap')}</dt>
                    <dd className="rg-admin-summary__value">
                      {global.capUsd === null ? t('adminArea.aiUsage.noCap') : `US$ ${formatNumber(global.capUsd, lang, 2)}`}
                    </dd>
                  </div>
                  <div className="rg-admin-summary__item">
                    <dt className="rg-admin-summary__term">{t('adminArea.aiUsage.capUsed')}</dt>
                    <dd className="rg-admin-summary__value">
                      {global.percent === null ? '—' : `${formatNumber(global.percent, lang, 1)} %`}
                    </dd>
                  </div>
                </dl>
              )}
              {global?.reached && (
                <Text>
                  <Badge tone="danger">{t('adminArea.aiUsage.capReached')}</Badge>
                </Text>
              )}
            </Section>

            <Section title={t('adminArea.aiUsage.usersTitle', { count: users.length })}>
              <LiveRegion>{done}</LiveRegion>
              {usage.isLoading && (
                <LiveRegion>
                  <Text>{t('adminArea.aiUsage.loading')}</Text>
                </LiveRegion>
              )}
              {usage.isError && (
                <LoadErrorState title={t('adminArea.aiUsage.loadError')} onRetry={() => void usage.refetch()} />
              )}
              {!usage.isLoading && !usage.isError && users.length === 0 && (
                <EmptyState
                  icon={<Sparkles size={48} strokeWidth={1.5} aria-hidden="true" />}
                  headline={t('adminArea.aiUsage.emptyTitle')}
                  description={t('adminArea.aiUsage.emptyMessage')}
                  actionLabel={t('adminArea.aiUsage.showCurrentMonth')}
                  onAction={() => setMonth(thisMonth)}
                  headingLevel={3}
                />
              )}
              {!usage.isLoading && !usage.isError && users.length > 0 && (
                <AdminTable caption={t('adminArea.aiUsage.tableCaption', { month })}>
                  <thead>
                    <tr>
                      <th scope="col">{t('adminArea.aiUsage.col.user')}</th>
                      <th scope="col" className="rg-admin-table__numeric">{t('adminArea.aiUsage.col.calls')}</th>
                      <th scope="col" className="rg-admin-table__numeric">{t('adminArea.aiUsage.col.errors')}</th>
                      <th scope="col" className="rg-admin-table__numeric">{t('adminArea.aiUsage.col.credits')}</th>
                      <th scope="col" className="rg-admin-table__numeric">{t('adminArea.aiUsage.col.topups')}</th>
                      <th scope="col" className="rg-admin-table__numeric">{t('adminArea.aiUsage.col.cost')}</th>
                      <th scope="col">{t('adminArea.aiUsage.col.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.userId}>
                        <th scope="row">{user.email ?? user.userId}</th>
                        <td className="rg-admin-table__numeric">{formatNumber(user.calls, lang)}</td>
                        <td className="rg-admin-table__numeric">{formatNumber(user.errorCalls, lang)}</td>
                        <td className="rg-admin-table__numeric">{formatNumber(user.creditsUsed, lang, 2)}</td>
                        <td className="rg-admin-table__numeric">
                          {formatNumber(user.topupCredits, lang)}
                          {(user.welcomeCredits ?? 0) > 0 && user.welcomeExpiresAt && (
                            <Text variant="caption" className="rg-admin-table__muted">
                              {t('adminArea.aiUsage.welcomeIncluded', {
                                credits: formatNumber(user.welcomeCredits ?? 0, lang),
                                remaining: formatNumber(user.welcomeRemaining ?? user.welcomeCredits ?? 0, lang, 1),
                                date: formatDate(user.welcomeExpiresAt, lang),
                              })}
                            </Text>
                          )}
                        </td>
                        <td className="rg-admin-table__numeric">US$ {formatNumber(user.costUsd, lang, 4)}</td>
                        <td>
                          {canTopUp ? (
                            <Button variant="secondary" size="sm" onClick={() => askTopup(user)}>
                              {t('adminArea.aiUsage.topup')}
                            </Button>
                          ) : (
                            <Text variant="caption" className="rg-admin-table__muted">{t('adminArea.aiUsage.pastMonth')}</Text>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </AdminTable>
              )}
            </Section>

            <AdminConfirmDialog
              request={confirm}
              onClose={() => setConfirm(null)}
              errorMessage={t('adminArea.aiUsage.topupFailed')}
            >
              <Input
                type="number"
                label={t('adminArea.aiUsage.credits')}
                helperText={t('adminArea.aiUsage.creditsHint')}
                value={credits}
                min={1}
                max={10000}
                step={1}
                inputMode="numeric"
                onChange={(e) => setCredits(e.target.value)}
                fullWidth
              />
            </AdminConfirmDialog>
          </AdminArea>
        </PageContent>
      }
    />
  );
}

export default AdminAiUsagePage;
